import { _Vue } from './index';
import { hasClass, addClass, removeClass } from '../../utils/dom';
import ZmCheckbox from '../checkbox';
import FilterPanel from './filter-panel.vue';
import LayoutObserver from './layout-observer';
import { mapStates } from './store/helper';

const getAllColumns = (columns) => {
  const result = [];
  columns.forEach((column) => {
    if (column.children) {
      result.push(column);
      result.push.apply(result, getAllColumns(column.children));
    } else {
      result.push(column);
    }
  });
  return result;
};

const convertToRows = (originColumns, table) => {
  let maxLevel = 1;
  const traverse = (column, parent) => {
    if (parent) {
      column.level = parent.level + 1;
      if (maxLevel < column.level) {
        maxLevel = column.level;
      }
    }
    if (column.children) {
      let colSpan = 0;
      column.children.forEach((subColumn) => {
        traverse(subColumn, column);
        colSpan += subColumn.colSpan;
      });
      column.colSpan = colSpan;
    } else {
      column.colSpan = 1;
    }
  };

  originColumns.forEach((column) => {
    column.level = 1;
    traverse(column);
  });

  const rows = [];
  for (let i = 0; i < maxLevel; i++) {
    rows.push([]);
  }

  const allColumns = getAllColumns(originColumns);

  allColumns.forEach((column) => {
    if (column.type === 'expand' && table.forceExpand) {
      return;
    }
    if (!column.children) {
      column.rowSpan = maxLevel - column.level + 1;
    } else {
      column.rowSpan = 1;
    }
    rows[column.level - 1].push(column);
  });

  return rows;
};

export default {
  name: 'ZmTableHeader',

  mixins: [LayoutObserver],

  render(h) {
    const fixed = this.fixed;
    const table = this.table;
    const originColumns = fixed
      ? fixed === 'right'
        ? this.rightFixedColumns
        : this.fixedColumns
      : this.store.states.originColumns;

    let columns = fixed
      ? fixed === 'right'
        ? this.rightFixedLeafColumns
        : this.fixedLeafColumns
      : this.columns;

    columns = columns.filter((column) => {
      if (column.type === 'expand' && table.forceExpand) {
        return false;
      }
      return true;
    });

    const columnRows = convertToRows(originColumns, table);
    // 是否拥有多级表头
    const isGroup = columnRows.length > 1;
    if (isGroup) table.isGroup = true;

    return this.renderTable(columns, columnRows, isGroup, h);
  },

  props: {
    fixed: String,
    store: {
      required: true
    },
    border: Boolean,
    adjustColumn: [Boolean, Object],
    defaultSort: {
      type: Object,
      default() {
        return {
          prop: '',
          order: ''
        };
      }
    }
  },

  inject: ['table'],

  components: {
    ZmCheckbox
  },

  computed: {
    bodyWidth() {
      return this.table.$el ? this.table.$el.clientWidth : 0;
    },

    hasGutter() {
      return !this.fixed && this.tableLayout.gutterWidth;
    },

    ...mapStates({
      columns: 'columns',
      isAllSelected: 'isAllSelected',
      fixedLeafColumns: 'fixedLeafColumns',
      fixedColumns: 'fixedColumns',
      rightFixedLeafColumns: 'rightFixedLeafColumns',
      rightFixedColumns: 'rightFixedColumns',
      leftFixedLeafCount: 'fixedLeafColumnsLength',
      rightFixedLeafCount: 'rightFixedLeafColumnsLength',
      columnsCount: states => states.columns.length,
      leftFixedCount: states => states.fixedColumns.length,
      rightFixedCount: states => states.rightFixedColumns.length
    })
  },

  created() {
    this.filterPanels = {};
  },

  mounted() {
    // nextTick 是有必要的 https://github.com/ElemeFE/element/pull/11311
    this.$nextTick(() => {
      const { prop, order } = this.defaultSort;
      const init = true;
      this.store.commit('sort', { prop, order, init });
    });
  },

  beforeDestroy() {
    const panels = this.filterPanels;
    for (let prop in panels) {
      if (panels.hasOwnProperty(prop) && panels[prop]) {
        panels[prop].$destroy(true);
      }
    }
  },

  methods: {
    renderTable(columns, columnRows, isGroup, createElement) {
      return (
        <table
          class="zm-table__header"
          cellspacing="0"
          cellpadding="0"
          border="0">
          <colgroup>
            {
              columns.map(column => <col name={column.id} key={column.id}/>)
            }
            {
              this.hasGutter ? <col name="gutter"/> : ''
            }
          </colgroup>
          <thead class={[{ 'is-group': isGroup, 'has-gutter': this.hasGutter }]}>{
            this._l(columnRows, (columns, rowIndex) =>
              <tr
                style={this.getHeaderRowStyle(rowIndex)}
                class={this.getHeaderRowClass(rowIndex)}
              >
                {
                  columns.map((column, cellIndex) => (<th
                    colspan={column.colSpan}
                    rowspan={column.rowSpan}
                    on-mousemove={($event) => this.handleMouseMove($event, column)}
                    on-mouseout={this.handleMouseOut}
                    on-mousedown={($event) => this.handleMouseDown($event, column)}
                    on-click={($event) => this.handleHeaderClick($event, column)}
                    on-contextmenu={($event) => this.handleHeaderContextMenu($event, column)}
                    style={this.getHeaderCellStyle(rowIndex, cellIndex, columns, column)}
                    class={this.getHeaderCellClass(rowIndex, cellIndex, columns, column)}
                    id={column.id}
                    key={column.id}
                    aria-label={column.label}
                  >
                    <div
                      class={['cell', column.filteredValue && column.filteredValue.length > 0 ? 'highlight' : '', column.labelClassName]}>
                      {
                        column.renderHeader
                          ? column.renderHeader.call(this._renderProxy, createElement, {
                            column,
                            $index: cellIndex,
                            store: this.store,
                            _self: this.table.$vnode.context
                          })
                          : <span tabIndex="-1">{column.label}</span>
                      }
                      {
                        column.sortable ? (<span
                          class="caret-wrapper"
                          on-click={($event) => this.handleSortClick($event, column)}>
                          <button
                            tabIndex="-1"
                            class="sort-caret-btn"
                            type="button"
                            aria-label={column.label + ', Sort by ascending' + (column.order === 'ascending' ? ', Selected' : '')}
                            on-click={($event) => this.handleSortClick($event, column, 'ascending')}>
                            <i class="zm-icon-ascent"></i>
                          </button>
                          <button
                            tabIndex="-1"
                            class="sort-caret-btn"
                            type="button"
                            aria-label={column.label + ', Sort by descending' + (column.order === 'descending' ? ', Selected' : '')}
                            on-click={($event) => this.handleSortClick($event, column, 'descending')}>
                            <i class="zm-icon-descent"></i>
                          </button>
                        </span>) : ''
                      }
                      {
                        column.filterable ? (<button
                          class="zm-table__column-filter-trigger"
                          type="button"
                          tabIndex="-1"
                          on-click={($event) => this.handleFilterClick($event, column)}>
                          <i
                            class={['zm-icon-arrow-down', column.filterOpened ? 'zm-icon-arrow-up' : '']}>
                          </i>
                        </button>) : ''
                      }
                    </div>
                  </th>))
                }
                {
                  this.hasGutter ? <th class="gutter"><span class="zm-sr-only">Gutter</span></th> : ''
                }
              </tr>
            )
          }
          </thead>
        </table>
      );
    },

    isCellHidden(index, columns) {
      if (this.fixed) {
        return false;
      }
      let start = 0;
      for (let i = 0; i < index; i++) {
        start += columns[i].colSpan;
      }
      const after = start + columns[index].colSpan - 1;
      const layout = this.table.layout;
      return layout.scrollX && (after < this.leftFixedLeafCount || (start >= this.columnsCount - this.rightFixedLeafCount));
    },

    getHeaderRowStyle(rowIndex) {
      const headerRowStyle = this.table.headerRowStyle;
      if (typeof headerRowStyle === 'function') {
        return headerRowStyle.call(null, { rowIndex });
      }
      return headerRowStyle;
    },

    getHeaderRowClass(rowIndex) {
      const classes = [];

      const headerRowClassName = this.table.headerRowClassName;
      if (typeof headerRowClassName === 'string') {
        classes.push(headerRowClassName);
      } else if (typeof headerRowClassName === 'function') {
        classes.push(headerRowClassName.call(null, { rowIndex }));
      }

      return classes.join(' ');
    },

    getHeaderCellStyle(rowIndex, columnIndex, row, column) {
      const headerCellStyle = this.table.headerCellStyle || {};
      if (typeof headerCellStyle === 'function') {
        return headerCellStyle.call(null, {
          rowIndex,
          columnIndex,
          row,
          column
        });
      } else {
        headerCellStyle.maxWidth = `${column.realWidth || column.width}px`;
      }
      return headerCellStyle;
    },

    getHeaderCellClass(rowIndex, columnIndex, row, column) {
      const classes = [column.id, column.order, column.headerAlign, column.className, column.labelClassName];

      if (rowIndex === 0 && this.isCellHidden(columnIndex, row)) {
        classes.push('is-hidden');
      }

      if (!column.children) {
        classes.push('is-leaf');
      }

      if (column.sortable) {
        classes.push('is-sortable');
      }

      if (column.headerWordWrap === 'break') {
        classes.push('break');
      } else if (column.headerWordWrap === 'ellipsis') {
        classes.push('ellipsis');
      }

      const headerCellClassName = this.table.headerCellClassName;
      if (typeof headerCellClassName === 'string') {
        classes.push(headerCellClassName);
      } else if (typeof headerCellClassName === 'function') {
        classes.push(headerCellClassName.call(null, {
          rowIndex,
          columnIndex,
          row,
          column
        }));
      }
      return classes;
      // return classes.join(' ');
    },

    toggleAllSelection(event) {
      event && event.stopPropagation();
      this.store.commit('toggleAllSelection');
    },

    handleFilterClick(event, column) {
      event.stopPropagation();
      const target = event.target;
      let cell = target.tagName === 'TH' ? target : target.parentNode;
      cell = cell.querySelector('.zm-table__column-filter-trigger') || cell;
      const table = this.table;

      let filterPanel = this.filterPanels[column.id];

      if (filterPanel && column.filterOpened) {
        filterPanel.showPopper = false;
        return;
      }

      if (!filterPanel) {
        filterPanel = new _Vue(FilterPanel);
        this.filterPanels[column.id] = filterPanel;
        if (column.filterPlacement) {
          filterPanel.placement = column.filterPlacement;
        }
        filterPanel.table = table;
        filterPanel.cell = cell;
        filterPanel.column = column;
        !this.$isServer && filterPanel.$mount(document.createElement('div'));
      }

      setTimeout(() => {
        filterPanel.showPopper = true;
      }, 16);
    },

    handleHeaderClick(event, column) {
      if (!column.filters && column.sortable) {
        this.handleSortClick(event, column);
      } else if (column.filterable && !column.sortable) {
        this.handleFilterClick(event, column);
      }

      this.table.$emit('header-click', column, event);
    },

    handleHeaderContextMenu(event, column) {
      this.table.$emit('header-contextmenu', column, event);
    },

    handleMouseDown(event, column) {
      if (this.$isServer) return;
      if (column.children && column.children.length > 0) return;
      /* istanbul ignore if */
      const adjustColumn = this.adjustColumn;
      if (this.draggingColumn && (this.border || adjustColumn)) {
        this.dragging = true;

        this.table.resizeProxyVisible = true;

        const table = this.table;
        const tableEl = table.$el;
        const tableLeft = tableEl.getBoundingClientRect().left;
        const columnEl = this.$el.querySelector(`th.${column.id}`);
        const columnRect = columnEl.getBoundingClientRect();
        const minLeft = columnRect.left - tableLeft + (adjustColumn && adjustColumn.minWidth || 30);

        addClass(columnEl, 'noclick');

        this.dragState = {
          startMouseLeft: event.clientX,
          startLeft: columnRect.right - tableLeft,
          startColumnLeft: columnRect.left - tableLeft,
          tableLeft
        };

        const resizeProxy = table.$refs.resizeProxy;
        resizeProxy.style.left = this.dragState.startLeft + 'px';

        document.onselectstart = function() {
          return false;
        };
        document.ondragstart = function() {
          return false;
        };

        const handleMouseMove = (event) => {
          const deltaLeft = event.clientX - this.dragState.startMouseLeft;
          const proxyLeft = this.dragState.startLeft + deltaLeft;

          resizeProxy.style.left = Math.max(minLeft, proxyLeft) + 'px';
        };

        const handleMouseUp = () => {
          if (this.dragging) {
            const {
              startColumnLeft,
              startLeft
            } = this.dragState;
            const finalLeft = parseInt(resizeProxy.style.left, 10);
            const columnWidth = finalLeft - startColumnLeft;
            column.width = column.realWidth = columnWidth;
            table.$emit('header-dragend', column.width, startLeft - startColumnLeft, column, event);

            this.store.scheduleLayout();

            document.body.style.cursor = '';
            this.dragging = false;
            this.draggingColumn = null;
            this.dragState = {};

            table.resizeProxyVisible = false;
          }

          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
          document.onselectstart = null;
          document.ondragstart = null;

          setTimeout(function() {
            removeClass(columnEl, 'noclick');
          }, 0);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
      }
    },

    handleMouseMove(event, column) {
      if (column.children && column.children.length > 0) return;
      let target = event.target;
      while (target && target.tagName !== 'TH') {
        target = target.parentNode;
      }

      if (!column || !column.resizable) return;

      if (!this.dragging && (this.border || this.adjustColumn)) {
        let rect = target.getBoundingClientRect();

        const bodyStyle = document.body.style;
        if (rect.width > 12 && rect.right - event.pageX < 8) {
          bodyStyle.cursor = 'col-resize';
          if (hasClass(target, 'is-sortable')) {
            target.style.cursor = 'col-resize';
          }
          this.draggingColumn = column;
        } else if (!this.dragging) {
          bodyStyle.cursor = '';
          if (hasClass(target, 'is-sortable')) {
            target.style.cursor = 'pointer';
          }
          this.draggingColumn = null;
        }
      }
    },

    handleMouseOut() {
      if (this.$isServer) return;
      document.body.style.cursor = '';
    },

    toggleOrder({ order, sortOrders }) {
      if (order === '') return sortOrders[0];
      const index = sortOrders.indexOf(order || null);
      return sortOrders[index > sortOrders.length - 2 ? 0 : index + 1];
    },

    handleSortClick(event, column, givenOrder) {
      event.stopPropagation();
      let order = column.order === givenOrder
        ? null
        : (givenOrder || this.toggleOrder(column));

      let target = event.target;
      while (target && target.tagName !== 'TH') {
        target = target.parentNode;
      }

      if (target && target.tagName === 'TH') {
        if (hasClass(target, 'noclick')) {
          removeClass(target, 'noclick');
          return;
        }
      }

      if (!column.sortable) return;

      const states = this.store.states;
      let sortProp = states.sortProp;
      let sortOrder;
      const sortingColumn = states.sortingColumn;

      if (sortingColumn !== column || (sortingColumn === column && sortingColumn.order === null)) {
        if (sortingColumn) {
          sortingColumn.order = null;
        }
        states.sortingColumn = column;
        sortProp = column.property;
      }

      if (!order) {
        sortOrder = column.order = null;
      } else {
        sortOrder = column.order = order;
      }

      states.sortProp = sortProp;
      states.sortOrder = sortOrder;

      this.store.commit('changeSortCondition');
    }
  },

  data() {
    return {
      draggingColumn: null,
      dragging: false,
      dragState: {}
    };
  }
};
